import {
  FormAndJSONSchema,
  FormSection,
} from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import DevicesFieldArray from '@/ui/components/enaForms/DevicesFieldArray';
import FieldSet from '@/ui/components/enaForms/FieldSet';
import ProjectFileSelection from '@/ui/components/enaForms/ProjectFileSelection';
import { Card, ScrollSpyProps } from '@payaca/ambrosia';
import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';

import { sentenceCase } from '@payaca/utilities/stringUtilities';
import ReactMarkdown from 'react-markdown';
import DevicesFieldArrayReadOnly from '../DevicesFieldArrayReadOnly';
import FieldSetArray from '../FieldSetArray';
import FieldSetArrayReadonly from '../FieldSetArrayReadonly';
import ProjectFileSelectionReadonly from '../ProjectFileSelectionReadonly';
import { DevicesFieldArrayKeys } from './devices';

const ignoredJsonExtraTitles = ['USER ACCOUNT DERIVED INPUT']; // ignore the json_schema_extra title for these types

const FieldSetSorting: Record<string, string[]> = {
  existingDevices: [
    'deviceClass',
    'deviceSysRef',
    'enaLctDataset',
    'powerFactor',
    'isBeingRemoved',
  ],
  devicesToInstall: [
    'deviceClass',
    'deviceSysRef',
    'targetInstallDate',
    'generatorType',
    'energyStorageCapacity',
    'powerFactor',
  ],
};

// TODO: move this into ConnectionApplicationLayout?
/**
 * Sort data properties object keys, so fields are rendered in a specific order
 */
export const getSortedFieldSetDataProperties = (
  properties: Record<string, any>,
  namespace?: string
) => {
  if (!namespace) {
    return properties;
  }
  const splitNamespace = namespace.split('.');
  const firstKey = splitNamespace[0];
  if (!(firstKey in FieldSetSorting)) {
    return properties;
  }
  const sortingDefinitionArray = FieldSetSorting[firstKey];
  const propertyKeys = Object.keys(properties);
  propertyKeys.sort((a, b) => {
    const aIndex = sortingDefinitionArray.indexOf(a);
    const bIndex = sortingDefinitionArray.indexOf(b);
    return aIndex - bIndex;
  });
  const sortedDataProperties: any = {};
  propertyKeys.forEach((key: string) => {
    sortedDataProperties[key] = properties[key];
  });
  // add any missing keys not defined in the sort at the end, so we don't lose any data if schema changes
  const unsortedKeys = propertyKeys.filter(
    (x) => !sortingDefinitionArray.includes(x)
  );
  unsortedKeys.forEach((key: string) => {
    sortedDataProperties[key] = properties[key];
  });

  return sortedDataProperties;
};

export const getDependentFields = (
  data: any,
  field: string,
  fieldNamespace?: string
): string[] => {
  // these can then be cleared when the current field changes
  const dependenentFields: string[] = [];
  Object.entries<{
    json_schema_extra: { displayIf: null | { property: string } };
  }>(data.properties).forEach(([key, value]) => {
    const isDependent =
      value.json_schema_extra.displayIf?.property &&
      value.json_schema_extra.displayIf.property === field;
    if (isDependent) {
      dependenentFields.push([fieldNamespace, key].filter((x) => x).join('.'));
    }
  });
  return dependenentFields;
};

export const getFieldLabelFromFieldSchema = (fieldSchema: {
  json_schema_extra: { type: string; required: boolean; title?: string };
  title: string;
}) => {
  const title = sentenceCase(
    (!ignoredJsonExtraTitles.includes(fieldSchema.json_schema_extra.type) &&
      fieldSchema.json_schema_extra?.title) ||
      fieldSchema.title
  );
  return `${title}${fieldSchema.json_schema_extra.required ? ' *' : ''}`;
};

export function generateFormSectionsForScrollSpy({
  formSections,
  formSchema,
  formData = {},
  readonly = false,
}: {
  formSections: [string, FormSection][];
  formSchema: FormAndJSONSchema;
  formData?: Record<string, unknown>;
  readonly?: boolean;
}): ScrollSpyProps['sections'] {
  return formSections.map(([key, formSection]) => {
    const title = sentenceCase(formSection.title);
    return {
      label: title,
      slug: key,
      render: () => {
        return (
          <Card key={key}>
            <Card.Body>
              <div className="space-y-4">
                <Card.Title>{title}</Card.Title>
                <p>
                  <ReactMarkdown>{formSection.subtitle}</ReactMarkdown>
                </p>

                {(() => {
                  if (formSection.type === 'object') {
                    return (
                      <FieldSet
                        formSection={formSection}
                        namespace={key}
                        readonly={readonly}
                        definitions={formSchema.definitions}
                        formData={formData}
                      />
                    );
                  } else if (
                    formSection.type === 'array' &&
                    formSection.items?.oneOf &&
                    DevicesFieldArrayKeys.includes(key)
                  ) {
                    if (readonly) {
                      return (
                        <DevicesFieldArrayReadOnly
                          schemaSection={formSection}
                          fieldKey={key}
                          definitions={formSchema.definitions}
                          formData={formData}
                        />
                      );
                    } else {
                      return (
                        <DevicesFieldArray
                          schemaSection={formSection}
                          fieldKey={key}
                          definitions={formSchema.definitions}
                        />
                      );
                    }
                  } else if (
                    formSection.type === 'array' &&
                    formSection.items?.title === 'AttachmentV1'
                  ) {
                    if (readonly) {
                      return (
                        <ProjectFileSelectionReadonly
                          name={key}
                          formSection={formSection}
                          formData={formData}
                        />
                      );
                    } else {
                      return (
                        <ProjectFileSelection
                          name={key}
                          formSection={formSection}
                        />
                      );
                    }
                  } else if (
                    formSection.type === 'array' &&
                    formSection.items?.type === 'object'
                  ) {
                    if (readonly) {
                      return (
                        <FieldSetArrayReadonly
                          schemaSection={formSection}
                          fieldKey={key}
                          definitions={formSchema.definitions}
                          formData={formData}
                        />
                      );
                    } else {
                      return (
                        <FieldSetArray
                          schemaSection={formSection}
                          fieldKey={key}
                          definitions={formSchema.definitions}
                        />
                      );
                    }
                  } else {
                    return (
                      <ErrorMessage
                        message={`Unable to display section (${key}). Please contact support.`}
                      />
                    );
                  }
                })()}
              </div>
            </Card.Body>
          </Card>
        );
      },
    };
  });
}
